/**
 * Machine Translation Engine
 * Multi-provider abstraction layer for MT services
 */

export interface MTProvider {
  getName(): string
  isAvailable(): boolean
  translate(text: string, sourceLang: string, targetLang: string): Promise<string>
  translateBatch(texts: string[], sourceLang: string, targetLang: string): Promise<string[]>
  getUsage(): number
  getMonthlyLimit(): number
  resetUsage(): void
}

export interface MTTranslation {
  provider: string
  translation: string
  confidence: number
  error?: string
}

export class MTEngine {
  private providers: MTProvider[] = []

  /**
   * Register a new MT provider
   */
  registerProvider(provider: MTProvider): void {
    if (provider.isAvailable()) {
      this.providers.push(provider)
      console.log(`✅ Registered MT provider: ${provider.getName()}`)
    } else {
      console.warn(`⚠️ Provider ${provider.getName()} not available`)
    }
  }

  /**
   * Get all registered providers
   */
  getProviders(): MTProvider[] {
    return this.providers
  }

  /**
   * Get list of available provider names
   */
  getAvailableProviders(): string[] {
    return this.providers.map(p => p.getName())
  }

  /**
   * Translate text with a specific provider
   */
  async translateWith(
    providerName: string,
    text: string,
    sourceLang: string,
    targetLang: string
  ): Promise<string> {
    const provider = this.providers.find(p => p.getName() === providerName)
    
    if (!provider) {
      throw new Error(`Provider "${providerName}" not found`)
    }

    return await provider.translate(text, sourceLang, targetLang)
  }

  /**
   * Translate text with all available providers
   */
  async translateWithAll(
    text: string,
    sourceLang: string,
    targetLang: string
  ): Promise<MTTranslation[]> {
    if (!text || text.trim().length === 0) {
      return []
    }

    const results: MTTranslation[] = []

    for (const provider of this.providers) {
      try {
        const translation = await provider.translate(text, sourceLang, targetLang)
        
        // Calculate confidence score (simple heuristic)
        const confidence = this.calculateConfidence(
          text,
          translation,
          provider.getName()
        )

        results.push({
          provider: provider.getName(),
          translation,
          confidence
        })
      } catch (error) {
        console.error(`Error with ${provider.getName()}:`, error)
        results.push({
          provider: provider.getName(),
          translation: '',
          confidence: 0,
          error: error instanceof Error ? error.message : 'Translation failed'
        })
      }
    }

    // Sort by confidence (highest first)
    return results.sort((a, b) => b.confidence - a.confidence)
  }

  /**
   * Batch translate with a specific provider
   */
  async translateBatchWith(
    providerName: string,
    texts: string[],
    sourceLang: string,
    targetLang: string
  ): Promise<string[]> {
    const provider = this.providers.find(p => p.getName() === providerName)
    
    if (!provider) {
      throw new Error(`Provider "${providerName}" not found`)
    }

    return await provider.translateBatch(texts, sourceLang, targetLang)
  }

  /**
   * Get best translation from all providers
   */
  async getBestTranslation(
    text: string,
    sourceLang: string,
    targetLang: string
  ): Promise<MTTranslation> {
    const results = await this.translateWithAll(text, sourceLang, targetLang)
    
    if (results.length === 0) {
      throw new Error('No MT providers available')
    }

    // Return highest confidence translation
    return results[0]
  }

  /**
   * Calculate confidence score for a translation
   * Simple heuristic based on provider quality and translation characteristics
   */
  private calculateConfidence(
    source: string,
    translation: string,
    providerName: string
  ): number {
    let confidence = 80 // Base confidence

    // Provider quality adjustments
    const providerScores: Record<string, number> = {
      'DeepL': 10,
      'Google Translate': 5,
      'MyMemory (Free)': 0
    }
    confidence += providerScores[providerName] || 0

    // Length similarity check
    const lengthRatio = translation.length / source.length
    if (lengthRatio < 0.5 || lengthRatio > 2.0) {
      confidence -= 10
    }

    // Empty translation check
    if (!translation || translation.trim().length === 0) {
      return 0
    }

    // Ensure confidence is between 0-100
    return Math.max(0, Math.min(100, confidence))
  }

  /**
   * Get usage statistics for all providers
   */
  getUsageStats(): Array<{
    provider: string
    usage: number
    limit: number
    percentage: number
  }> {
    return this.providers.map(provider => {
      const usage = provider.getUsage()
      const limit = provider.getMonthlyLimit()
      const percentage = Math.round((usage / limit) * 100)

      return {
        provider: provider.getName(),
        usage,
        limit,
        percentage
      }
    })
  }

  /**
   * Reset usage for all providers
   */
  resetAllUsage(): void {
    this.providers.forEach(provider => {
      provider.resetUsage()
    })
    console.log('✅ Reset usage for all MT providers')
  }

  /**
   * Check if any providers are available
   */
  hasProviders(): boolean {
    return this.providers.length > 0
  }

  /**
   * Get total number of providers
   */
  getProviderCount(): number {
    return this.providers.length
  }
}

export default MTEngine